import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBlockedUserIds } from "@/hooks/hub/useHubBlocks";

/**
 * LR HUB "Recently Active" rail box.
 *
 * Data source: `hub_recently_active` RPC — Hub members ordered by their most
 * recent APPROVED post or comment. Members with no activity yet still come
 * back (ordered by when they joined the Hub), so the box is never empty.
 *
 * Display-safe fields only (Hub display name / handle / approved photo /
 * badge) — same identity path as the feed. Hub-isolated.
 */

export interface HubRecentlyActiveMember {
  user_id: string;
  display_name: string;
  handle: string;
  avatar_path: string | null;
  hub_badge: string | null;
  /** Most recent approved post/comment; epoch when the member never posted. */
  last_active_at: string;
}

const EPOCH_YEAR = 1971;

/** True when the member has never posted or commented (epoch sentinel). */
export function hubHasActivity(m: HubRecentlyActiveMember): boolean {
  const t = new Date(m.last_active_at);
  return !Number.isNaN(t.getTime()) && t.getUTCFullYear() > EPOCH_YEAR;
}

export function useHubRecentlyActive(limit = 30) {
  const { blockedIds } = useBlockedUserIds();
  return useQuery({
    queryKey: ["hub-recently-active", limit, blockedIds.join(",")],
    staleTime: 60_000,
    queryFn: async (): Promise<HubRecentlyActiveMember[]> => {
      const { data, error } = await supabase.rpc("hub_recently_active", { _limit: limit });
      if (error) throw error;
      const blocked = new Set(blockedIds);
      return ((data ?? []) as HubRecentlyActiveMember[]).filter((m) => !blocked.has(m.user_id));
    },
  });
}

export default useHubRecentlyActive;
